import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import RadioGroup from "./RadioGroup";

interface Option {
  value: string;
  label: string;
}

interface SelectWithRadioProps {
  label: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  id?: string;
  placeholder?: string;
  maxHeight?: string;
  disabled?: boolean;
  showAbove?: boolean;
  className?: string;
  labelClassName?: string;
}

const SelectWithRadio: React.FC<SelectWithRadioProps> = ({
  label,
  options,
  value,
  onChange,
  error,
  required = false,
  id,
  placeholder = "Select an option",
  maxHeight = "450px",
  disabled = false,
  showAbove = true,
  className = "",
  labelClassName = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<Option[]>([
    ...options,
  ]);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectedOption = options.find((option) => option.value === value);
  const showSearch = options.length >= 5;

  // Track window resize events
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Position dropdown absolutely in portal
  useLayoutEffect(() => {
    if (isOpen && selectRef.current) {
      const selectRect = selectRef.current.getBoundingClientRect();
      const dropdownWidth = selectRect.width;
      let style: React.CSSProperties = {
        position: "absolute",
        left: Math.round(selectRect.left),
        width: Math.round(dropdownWidth),
        minWidth: Math.round(dropdownWidth),
        zIndex: 9999,
      };
      if (showAbove) {
        // Anchor the bottom of the popup to the top of the select input
        style.bottom = window.innerHeight - Math.round(selectRect.top);
      } else {
        // Normal dropdown below
        style.top = Math.round(selectRect.bottom);
      }
      setDropdownStyle(style);
    }
  }, [isOpen, showAbove, maxHeight, windowHeight]);

  // Filter options based on search term while preserving original order
  useEffect(() => {
    // Always maintain the original order from the options array
    if (searchTerm.trim() === "") {
      setFilteredOptions([...options]);
    } else {
      // Filter while preserving the original order
      // Convert search term to lowercase for case-insensitive comparison
      const searchLower = searchTerm.toLowerCase();

      const filtered = options.filter((option) => {
        // Skip the placeholder option (empty value with placeholder text)
        if (option.value === "" && option.label === placeholder) {
          return false;
        }
        // Check if the option label includes the search term (case insensitive)
        return option.label.toLowerCase().includes(searchLower);
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, placeholder]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // Reset search and filtered options when dropdown closes
    if (!isOpen) {
      setSearchTerm("");
      setFilteredOptions([...options]);
    }
  }, [isOpen, showSearch, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        selectRef.current &&
        !selectRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div>
      {label && (
        <label
          htmlFor={id}
          className="block text-[#1E1E1E] text-[14px] font-[500] mb-1"
        >
          {label}
          {required && <span className="text-[#DC2626]">*</span>}
        </label>
      )}
      <div className="relative" id={id} ref={selectRef}>
        <div
          className={`w-full h-[42px] px-3 border ${
            error ? "border-[#FF3B30]" : "border-[#E0E0E0]"
          } rounded-[6px] flex items-center justify-between ${
            disabled ? "" : "cursor-pointer"
          } ${className}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <span
            className={`text-[#1E1E1E] text-[14px] font-[400] ${
              value ? "" : "text-[#B3B3B3]"
            } ${labelClassName}`}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          {!disabled && (
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              ></path>
            </svg>
          )}
        </div>

        {isOpen &&
          typeof window !== "undefined" &&
          ReactDOM.createPortal(
            <div
              ref={dropdownRef}
              className={`bg-white border border-gray-200 rounded-md shadow-lg scrollbar-custom ${
                showAbove ? "" : ""
              }`}
              style={{
                ...dropdownStyle,
                maxHeight:
                  windowHeight < 630
                    ? `${Math.min(parseInt(maxHeight), windowHeight * 0.4)}px`
                    : maxHeight,
                overflowY: "auto",
              }}
            >
              {showSearch && (
                <div className="sticky top-0 p-2 bg-white border-b border-gray-200 z-10">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg
                        className="h-4 w-4 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="w-full pl-10 pr-3 py-2 rounded-md text-sm focus:outline-none"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              )}
              <RadioGroup
                label=""
                options={filteredOptions}
                value={value}
                onChange={(newValue) => {
                  if (!disabled) {
                    onChange(newValue);
                    setIsOpen(false);
                  }
                }}
                id={id}
              />
              {filteredOptions.length === 0 && (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No options found
                </div>
              )}
            </div>,
            document.body
          )}
      </div>
      {error && (
        <p className="mt-0 text-[12px] font-[500] text-[#FF3B30]">{error}</p>
      )}
    </div>
  );
};

export default SelectWithRadio;
